import BusinessCenterOutlinedIcon from '@mui/icons-material/BusinessCenterOutlined';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import { Card, Grid, Stack } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { useCallback, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { ClosureDTO, getClosures, IOurEntity, ourEntityAllList } from 'wisestep-api';
import {
  ControlledTextInput,
  formatDateInDayMonthYear,
  WsButton,
  WsDatePicker,
  WsFilterDialog,
  formattedDate,
  WsColors,
} from 'wisestep-base';

import { ClientAutocomplete } from '../../../components/ClientAutocomplete';
import WsCheckBox from '../../../components/WsCheckBox/WsCheckBox';
import WsCheckmarkSelect from '../../../components/WsCheckmarkSelect';
import WsMultiSelectSmartFilter from '../../../components/WsMultiSelectSmartFilter';
import { ClosureFiltersForm, mapClosureFiltersToDTO } from '../../closures/helper/ClosureHelper';
import ICandidate from '../types/ICandidate';

const defaultValues: ClosureFiltersForm = {
  candidateSearch: null,
  ourEntities: [],
  clients: [],
};

const pageSize = 5;

const InvoiceCandidateList = () => {
  const form = useForm<ClosureFiltersForm>({ defaultValues });
  const [closureListData, setClosureListData] = useState<ClosureDTO[]>([]);
  const [pageNo, setPageNo] = useState<number>(0);
  const [totalRecord, setTotalRecord] = useState<number>(0);
  const [ourEntities, setOurEntities] = useState<IOurEntity[]>([]);
  const fromJoiningDate = form.watch('fromJoiningDate');
  const toJoiningDate = form.watch('toJoiningDate');
  const fromJoiningDateString = String(fromJoiningDate);
  const toJoiningDateString = String(toJoiningDate);
  const navigate = useNavigate();
  const tableData: Record<string, unknown>[] = [];
  let candidates: ICandidate[] = [];

  const onClosureSelect = (data, selected: boolean) => {
    if (selected) {
      candidates.push({
        data: {
          candidateName: data.row.candidateName,
          candidateEmail: data.row.candidateEmail,
          commissionValue: data.row.commissionValue,
          compensationAmount: data.row.compensationAmount,
          currencyId: '',
          currencySymbol: '',
          feeArrangementAmount: data.row.feeArrangementAmount,
          id: data.row.id,
          name: '',
        },
        isEditable: true,
      });
    } else {
      candidates = candidates.filter((obj: ICandidate) => {
        return obj.data.id !== data.row.id;
      });
    }
  };

  const columnsName: GridColDef[] = [
    {
      field: 'select',
      headerName: '',
      hide: false,
      renderCell: (params) => {
        return (
          <WsCheckBox
            onClick={(e) => onClosureSelect(params, e)}
            name="selectedClosure"
            label={''}
          ></WsCheckBox>
        );
      },
    },
    {
      field: 'id',
      headerName: 'id',
      hide: true,
    },
    {
      field: 'candidateName',
      headerName: 'Candidate Name',
      flex: 1,
    },
    {
      field: 'ourEntityName',
      headerName: 'Our Entity',
      flex: 1,
    },
    {
      field: 'clientName',
      headerName: 'Client Name',
      flex: 1,
    },
    {
      field: 'jobTitle',
      headerName: 'Job Title',
      flex: 1,
    },
    {
      field: 'compensationAmount',
      headerName: 'Compensation',
      flex: 1,
    },
    {
      field: 'offerDate',
      headerName: 'Offer Date',
      flex: 1,
      renderCell: (params) => {
        const date = params.row['offerDate']
          ? formatDateInDayMonthYear(new Date(params.row['offerDate']))
          : '';
        return <>{date}</>;
      },
    },
    {
      field: 'joiningDate',
      headerName: 'Joining Date',
      flex: 1,
      renderCell: (params) => {
        const date = params.row['joiningDate']
          ? formatDateInDayMonthYear(new Date(params.row['joiningDate']))
          : '';
        return <>{date}</>;
      },
    },
    {
      field: 'employmentGuaranteeEndDate',
      headerName: 'Employment guarantee end date',
      flex: 1,
      renderCell: (params) => {
        const date = params.row['employmentGuaranteeEndDate']
          ? formatDateInDayMonthYear(new Date(params.row['employmentGuaranteeEndDate']))
          : '';
        return <>{date}</>;
      },
    },
    { field: 'empGuaranteeStatus', headerName: 'Employment guarantee status', flex: 1 },
  ];
  closureListData.forEach((closureListData, index) => {
    tableData.push({
      id: closureListData.closureId,
      candidateName: closureListData.candidateName,
      candidateEmail: closureListData.candidateEmail,
      jobTitle: closureListData.jobTitle,
      clientName: closureListData.clientName,
      ourEntityName: closureListData.ourEntityName,
      invoiceStatus: closureListData.invoiceStatus,
      invoiceReference: closureListData.invoiceReference,
      joiningDate: closureListData.joiningDate,
      offerDate: closureListData.offerDate,
      compensationAmount: closureListData.compensationAmount,
      feeArrangementAmount: closureListData.feeArrangementAmount,
      commissionValue: closureListData.commissionValue,
      clientId: closureListData.clientId,
      ourEntityId: closureListData.ourEntityId,
      employmentGuaranteeEndDate: closureListData.employmentGuaranteeEndDate,
      empGuaranteeStatus: 0,
      index: index,
    });
  });

  const selectedJoiningDates =
    fromJoiningDate && toJoiningDate
      ? `${formattedDate(fromJoiningDateString)} to ${formattedDate(toJoiningDateString)}`
      : fromJoiningDate
      ? `From ${formattedDate(fromJoiningDateString)}`
      : toJoiningDate
      ? `Till ${formattedDate(toJoiningDateString)}`
      : 'All';

  const updateClosures = useCallback(
    async (filters) => {
      const dto = mapClosureFiltersToDTO(filters);
      const response = await getClosures({
        ...dto,
        page: pageNo,
        size: pageSize,
      });
      setTotalRecord(response.data.totalElements);
      setClosureListData(response.data.content);
    },
    [pageNo]
  );

  useEffect(() => {
    (async () => {
      await updateClosures(form.getValues());
    })();
  }, [form, pageNo, updateClosures]);

  useEffect(() => {
    (async () => {
      const response = await ourEntityAllList();
      setOurEntities(response.data.data);
    })();
  }, []);

  const onSearch = (data) => {
    updateClosures(data);
  };

  const selectCandidates = () => {
    navigate('/invoice/add', {
      state: {
        selectedCandidates: candidates,
      },
    });
  };

  return (
    <>
      <FormProvider {...form}>
        <Card elevation={0} sx={{ p: 2 }}>
          <form onSubmit={form.handleSubmit(onSearch)}>
            <Grid container rowSpacing={2} columnSpacing={1}>
              <Grid item xs={10}>
                <ControlledTextInput
                  fullWidth
                  name="candidateSearch"
                  placeholder="Search Candidate..."
                  size="small"
                />
              </Grid>
              <Grid item xs={1}>
                <WsButton
                  fullWidth
                  variant="text"
                  size="large"
                  type="reset"
                  onClick={() => {
                    form.reset(defaultValues);
                    onSearch(defaultValues);
                  }}
                >
                  Reset
                </WsButton>
              </Grid>
              <Grid item xs={1}>
                <WsButton fullWidth size="large" type="submit">
                  Search
                </WsButton>
              </Grid>
              <Grid item xs={2.5}>
                <WsMultiSelectSmartFilter
                  label="Our Entity"
                  name="ourEntities"
                  icon={<BusinessCenterOutlinedIcon />}
                >
                  <WsCheckmarkSelect
                    name="ourEntities"
                    options={ourEntities}
                    getLabel={(option) => option.Name}
                  />
                </WsMultiSelectSmartFilter>
              </Grid>
              <Grid item xs={2.5}>
                <WsMultiSelectSmartFilter
                  label="Client Name"
                  name="clients"
                  icon={<BusinessOutlinedIcon />}
                >
                  <ClientAutocomplete name="clients" multiple />
                </WsMultiSelectSmartFilter>
              </Grid>
              <Grid item xs={2.5}>
                <WsFilterDialog
                  label="Joining Date"
                  name={selectedJoiningDates}
                  icon={<CalendarMonthOutlinedIcon />}
                >
                  <Stack spacing={2} direction="row" py={2} px={1}>
                    <WsDatePicker name="fromJoiningDate" label="From Date" />
                    <WsDatePicker name="toJoiningDate" label="To Date" minDate={fromJoiningDate} />
                  </Stack>
                </WsFilterDialog>
              </Grid>
            </Grid>
            <Grid height={500} xs={12} bgcolor={WsColors.White} mt={1}>
              <DataGrid
                columns={columnsName}
                rows={tableData}
                pageSize={pageSize}
                onPageChange={(page) => setPageNo(page)}
                paginationMode="server"
                rowCount={totalRecord}
              />
            </Grid>
            <Stack
              p={1}
              spacing={2}
              direction="row"
              sx={{ display: 'flex', justifyContent: 'right' }}
            >
              <WsButton variant="contained" size="large" type="button" onClick={selectCandidates}>
                {`Continue`}
              </WsButton>
            </Stack>
          </form>
        </Card>
      </FormProvider>
    </>
  );
};

export default InvoiceCandidateList;
